import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AppProvider, useApp } from "@/context/AppContext";
import { StoreProvider } from "@/context/DataStore";
import { Toaster } from "@/components/ui/sonner";
import { IconTips } from "@/components/common/IconTips";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { homePathFor } from "@/lib/nav";
import { lazyPage } from "@/lib/lazyPage";

import Login from "@/pages/Login";
const Dashboard = lazyPage(() => import("@/pages/Dashboard"));
const Patients = lazyPage(() => import("@/pages/Patients"));
const Orders = lazyPage(() => import("@/pages/Orders"));
const Workspace = lazyPage(() => import("@/pages/Workspace"));
const OrderDetail = lazyPage(() => import("@/pages/OrderDetail"));
const OrderEditRequests = lazyPage(() => import("@/pages/OrderEditRequests"));
const OrderNew = lazyPage(() => import("@/pages/OrderNew"));
const Estimates = lazyPage(() => import("@/pages/Estimates"));
const Specimens = lazyPage(() => import("@/pages/Specimens"));
const Dispatch = lazyPage(() => import("@/pages/Dispatch"));
const Rejections = lazyPage(() => import("@/pages/Rejections"));
const Results = lazyPage(() => import("@/pages/Results"));
const Verification = lazyPage(() => import("@/pages/Verification"));
const Reports = lazyPage(() => import("@/pages/Reports"));
const HomeCollection = lazyPage(() => import("@/pages/HomeCollection"));
const ManualDispatch = lazyPage(() => import("@/pages/ManualDispatch"));
const Executives = lazyPage(() => import("@/pages/Executives"));
const CollectionCharges = lazyPage(() => import("@/pages/CollectionCharges"));
const LiveTracking = lazyPage(() => import("@/pages/LiveTracking"));
const Zones = lazyPage(() => import("@/pages/Zones"));
const Payments = lazyPage(() => import("@/pages/Payments"));
const Expenses = lazyPage(() => import("@/pages/Expenses"));
const Receipts = lazyPage(() => import("@/pages/Receipts"));
const DayClosingSummary = lazyPage(() => import("@/pages/DayClosingSummary"));
const DayClosing = lazyPage(() => import("@/pages/DayClosing"));
const Commissions = lazyPage(() => import("@/pages/Commissions"));
const Analytics = lazyPage(() => import("@/pages/Analytics"));
const Statistics = lazyPage(() => import("@/pages/Statistics"));
const ApprovalSettings = lazyPage(() => import("@/pages/ApprovalSettings"));
const Outsourcing = lazyPage(() => import("@/pages/Outsourcing"));
const CriticalValues = lazyPage(() => import("@/pages/CriticalValues"));
const Quality = lazyPage(() => import("@/pages/Quality"));
const Capa = lazyPage(() => import("@/pages/Capa"));
const CancelledOrders = lazyPage(() => import("@/pages/CancelledOrders"));
const Audit = lazyPage(() => import("@/pages/Audit"));
const Franchise = lazyPage(() => import("@/pages/Franchise"));
const ReferenceCentrePrices = lazyPage(() => import("@/pages/ReferenceCentrePrices"));
const B2BCentres = lazyPage(() => import("@/pages/B2BCentres"));
const B2BOrders = lazyPage(() => import("@/pages/B2BOrders"));
const B2BInvoices = lazyPage(() => import("@/pages/B2BInvoices"));
const B2BBusinessReport = lazyPage(() => import("@/pages/B2BBusinessReport"));
const B2BReports = lazyPage(() => import("@/pages/B2BReports"));
const PendingReports = lazyPage(() => import("@/pages/PendingReports"));
const TatAnalysis = lazyPage(() => import("@/pages/TatAnalysis"));
const TatTickets = lazyPage(() => import("@/pages/TatTickets"));
const CenterPrices = lazyPage(() => import("@/pages/CenterPrices"));
const Doctors = lazyPage(() => import("@/pages/Doctors"));
const ReferringCentres = lazyPage(() => import("@/pages/ReferringCentres"));
const Tests = lazyPage(() => import("@/pages/Tests"));
const TestBuilder = lazyPage(() => import("@/pages/TestBuilder"));
const Departments = lazyPage(() => import("@/pages/Departments"));
const OrderSources = lazyPage(() => import("@/pages/OrderSources"));
const Wallet = lazyPage(() => import("@/pages/Wallet"));
const Pricing = lazyPage(() => import("@/pages/Pricing"));
const Centers = lazyPage(() => import("@/pages/Centers"));
const CityCatalogue = lazyPage(() => import("@/pages/CityCatalogue"));
const Users = lazyPage(() => import("@/pages/Users"));
const Settings = lazyPage(() => import("@/pages/Settings"));
const Enquiries = lazyPage(() => import("@/pages/Enquiries"));
const Tickets = lazyPage(() => import("@/pages/Tickets"));
const Carts = lazyPage(() => import("@/pages/Carts"));
const Prescriptions = lazyPage(() => import("@/pages/Prescriptions"));
const Banners = lazyPage(() => import("@/pages/Banners"));
const HomePopular = lazyPage(() => import("@/pages/HomePopular"));
const HomeSlotSettings = lazyPage(() => import("@/pages/HomeSlotSettings"));
const HealthCategories = lazyPage(() => import("@/pages/HealthCategories"));
const AppReleases = lazyPage(() => import("@/pages/AppReleases"));
const CityCatalogueIndex = lazyPage(() => import("@/pages/CityCatalogueIndex"));
const Coupons = lazyPage(() => import("@/pages/Coupons"));
const OffersRewards = lazyPage(() => import("@/pages/OffersRewards"));
const PushNotifications = lazyPage(() => import("@/pages/PushNotifications"));
const ReportTemplates = lazyPage(() => import("@/pages/ReportTemplates"));
const ReportPrintStyle = lazyPage(() => import("@/pages/ReportPrintStyle"));
const Signatures = lazyPage(() => import("@/pages/Signatures"));
const Inventory = lazyPage(() => import("@/pages/Inventory"));
const ItemRequests = lazyPage(() => import("@/pages/ItemRequests"));
const Help = lazyPage(() => import("@/pages/Help"));
const QrStock = lazyPage(() => import("@/pages/QrStock"));
const Pickups = lazyPage(() => import("@/pages/Pickups"));
const BusinessUnits = lazyPage(() => import("@/pages/BusinessUnits"));
const CentrePortal = lazyPage(() => import("@/pages/centre/CentrePortal"));
const MarketingDashboard = lazyPage(() => import("@/pages/marketing/MarketingDashboard"));
const MarketingExecutives = lazyPage(() => import("@/pages/marketing/MarketingExecutives"));
const MarketingExecutiveDetail = lazyPage(() => import("@/pages/marketing/MarketingExecutiveDetail"));
const CrmDashboard = lazyPage(() => import("@/pages/crm/CrmDashboard"));
const CrmOrders = lazyPage(() => import("@/pages/crm/CrmOrders"));
const BookScan = lazyPage(() => import("@/pages/crm/BookScan"));
const MarketingLeads = lazyPage(() => import("@/pages/marketing/MarketingLeads"));
const MarketingActivities = lazyPage(() => import("@/pages/marketing/MarketingActivities"));
const PartnerOnboarding = lazyPage(() => import("@/pages/marketing/PartnerOnboarding"));
const BusinessRevenue = lazyPage(() => import("@/pages/marketing/BusinessRevenue"));
const MarketingReports = lazyPage(() => import("@/pages/marketing/MarketingReports"));
const HardCopies = lazyPage(() => import("@/pages/HardCopies"));
const Radiology = lazyPage(() => import("@/pages/Radiology"));

// Customer Care, Leads & Follow-ups and Callbacks merged into Enquiries (2026-10-15):
// old links land on the right tab / bucket with the rest of their query kept.
function CrmMoved({ tab, bucket }) {
  const { search } = useLocation();
  const p = new URLSearchParams(search);
  if (tab && !p.get("tab")) p.set("tab", tab);
  if (bucket && !p.get("bucket")) p.set("bucket", bucket);
  const qs = p.toString();
  return <Navigate to={`/app/enquiries${qs ? `?${qs}` : ""}`} replace />;
}

// /app itself: the dashboard, or the first page of the menu for a role without one.
function HomeRedirect() {
  const { session } = useApp();
  return <Navigate to={homePathFor(session)} replace />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AppProvider>
        <StoreProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/app" element={<DashboardLayout />}>
                <Route index element={<HomeRedirect />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="patients" element={<Patients />} />
                <Route path="orders" element={<Orders />} />
                <Route path="order-edit-requests" element={<OrderEditRequests />} />
                <Route path="workspace" element={<Workspace />} />
                <Route path="orders/new" element={<OrderNew />} />
                <Route path="orders/:id/edit" element={<OrderNew />} />
                <Route path="estimates" element={<Estimates />} />
                <Route path="orders/:id" element={<OrderDetail />} />
                <Route path="leads" element={<CrmMoved />} />
                <Route path="specimens" element={<Specimens />} />
                <Route path="dispatch" element={<Dispatch />} />
                <Route path="rejections" element={<Rejections />} />
                <Route path="results" element={<Results />} />
                <Route path="pending-reports" element={<PendingReports />} />
                {/* the Radiology section's scan side of the same pages (2026-10-29) */}
                <Route path="scan-results" element={<Results key="scan" scope="scan" />} />
                <Route path="scan-pending-reports" element={<PendingReports key="scan" scope="scan" />} />
                <Route path="scan-verification" element={<Verification key="scan" scope="scan" />} />
                <Route path="tat-analysis" element={<TatAnalysis />} />
                <Route path="tat-tickets" element={<TatTickets />} />
                <Route path="verification" element={<Verification />} />
                <Route path="reports" element={<Reports />} />
                <Route path="hard-copies" element={<HardCopies />} />
                <Route path="home-collection" element={<HomeCollection />} />
                {/* the same order form under each section that starts one, so the menu stays put (2026-10-30; NewOrderButton) */}
                <Route path="home-collection/new" element={<OrderNew from="home" />} />
                <Route path="workspace/new" element={<OrderNew from="worklist" />} />
                <Route path="b2b-orders/new" element={<OrderNew from="b2b" />} />
                <Route path="manual-dispatch" element={<ManualDispatch />} />
                <Route path="executives" element={<Executives />} />
                <Route path="collection-charges" element={<CollectionCharges />} />
                <Route path="live-tracking" element={<LiveTracking />} />
                <Route path="zones" element={<Zones />} />
                <Route path="payments" element={<Payments />} />
                <Route path="expenses" element={<Expenses />} />
                <Route path="receipts" element={<Receipts />} />
                <Route path="day-closing" element={<DayClosing />} />
                <Route path="day-closing/summary" element={<DayClosingSummary />} />
                <Route path="commissions" element={<Commissions />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="statistics" element={<Statistics />} />
                <Route path="approval-settings" element={<ApprovalSettings />} />
                <Route path="outsourcing" element={<Outsourcing />} />
                <Route path="critical-values" element={<CriticalValues />} />
                <Route path="quality" element={<Quality />} />
                <Route path="capa" element={<Capa />} />
                <Route path="cancelled-orders" element={<CancelledOrders />} />
                <Route path="audit" element={<Audit />} />
                <Route path="radiology" element={<Radiology />} />
                <Route path="radiology/:tab" element={<Radiology />} />
                <Route path="franchise" element={<Franchise />} />
                {/* one kind of center since 2026-10-27: the old Reference Centers address opens B2B Centers */}
                <Route path="reference-centres" element={<Navigate to="/app/b2b-centres" replace />} />
                <Route path="reference-centres/:id/prices" element={<ReferenceCentrePrices />} />
                <Route path="b2b-centres" element={<B2BCentres />} />
                <Route path="b2b-centres/:id/prices" element={<ReferenceCentrePrices />} />
                <Route path="b2b-orders" element={<B2BOrders />} />
                <Route path="b2b-invoices" element={<B2BInvoices />} />
                <Route path="b2b-business-report" element={<B2BBusinessReport />} />
                <Route path="b2b-reports" element={<B2BReports />} />
                <Route path="b2b-patients" element={<Patients partnerKind="B2B" />} />
                <Route path="doctors" element={<Doctors />} />
                <Route path="referring-centres" element={<ReferringCentres />} />
                <Route path="tests" element={<Tests />} />
                <Route path="tests/:code" element={<TestBuilder />} />
                <Route path="departments" element={<Departments />} />
                <Route path="order-sources" element={<OrderSources />} />
                <Route path="wallet" element={<Wallet />} />
                <Route path="pricing" element={<Pricing />} />
                <Route path="centers" element={<Centers />} />
                <Route path="cities/:id/catalogue" element={<CityCatalogue />} />
                <Route path="centers/:id/prices" element={<CenterPrices />} />
                <Route path="users" element={<Users />} />
                <Route path="settings" element={<Settings />} />
                <Route path="enquiries" element={<Enquiries />} />
                <Route path="tickets" element={<Tickets />} />
                <Route path="carts" element={<Carts />} />
                <Route path="prescriptions" element={<Prescriptions />} />
                <Route path="banners" element={<Banners />} />
                <Route path="home-popular" element={<HomePopular />} />
                <Route path="home-collection-slots" element={<HomeSlotSettings />} />
                <Route path="health-categories" element={<HealthCategories />} />
                <Route path="app-releases" element={<AppReleases />} />
                <Route path="city-catalogue" element={<CityCatalogueIndex />} />
                <Route path="coupons" element={<Coupons />} />
                <Route path="offers" element={<OffersRewards />} />
                <Route path="callbacks" element={<CrmMoved bucket="callback" />} />
                <Route path="push" element={<PushNotifications />} />
                <Route path="report-templates" element={<ReportTemplates />} />
                <Route path="report-style" element={<ReportPrintStyle />} />
                <Route path="signatures" element={<Signatures />} />
                <Route path="inventory" element={<Inventory />} />
                <Route path="item-requests" element={<ItemRequests />} />
                <Route path="help" element={<Help />} />
                <Route path="qr-stock" element={<QrStock />} />
                <Route path="pickups" element={<Pickups />} />
                <Route path="care" element={<CrmMoved tab="today" />} />
                <Route path="business-units" element={<BusinessUnits />} />
                <Route path="centre" element={<CentrePortal />} />
                <Route path="centre/:page" element={<CentrePortal />} />
                {/* Internal Marketing CRM (2026-10-19) */}
                <Route path="marketing/dashboard" element={<MarketingDashboard />} />
                <Route path="marketing/executives" element={<MarketingExecutives />} />
                <Route path="marketing/executives/:id" element={<MarketingExecutiveDetail />} />
                {/* the CRM team's desk (2026-10-30) */}
                <Route path="crm" element={<CrmDashboard />} />
                <Route path="crm/orders" element={<CrmOrders />} />
                <Route path="crm/book-scan" element={<BookScan />} />
                {/* New Order / Home Collection from the CRM pages stay under CRM (NewOrderButton, 2026-10-30) */}
                <Route path="crm/new" element={<OrderNew from="crmDashboard" />} />
                <Route path="crm/orders/new" element={<OrderNew from="crm" />} />
                <Route path="marketing/leads" element={<MarketingLeads />} />
                <Route path="marketing/activities" element={<MarketingActivities />} />
                <Route path="marketing/onboarding" element={<PartnerOnboarding />} />
                <Route path="marketing/revenue" element={<BusinessRevenue />} />
                <Route path="marketing/reports" element={<MarketingReports />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
          <IconTips />
        </div>
        </StoreProvider>
      </AppProvider>
    </ThemeProvider>
  );
}

export default App;
